import React, {useMemo} from 'react'

import {useFormik} from 'formik'
import * as Yup from 'yup'

import {useAppDispatch} from '../../../hooks/redux'
import {closeModal} from '../../../redux/features/modal'

import {formatDateTime} from '../../../libs/formatter'
import {getTrackingInfoHistory} from '../../../libs/order'

import {ModalMeta} from '../../../types'

import sendIcon from '../../../../../commons/src/assets/icons/send.svg'

import ActionBar from '../../../../../commons/src/components/action-bar'
import Button from '../../../../../commons/src/components/button'
import FieldNote from '../../../../../commons/src/components/field-note'
import FormRow from '../../../../../commons/src/components/form-row'
import Headline from '../../../../../commons/src/components/headline'
import Icon from '../../../../../commons/src/components/icon'
import InputField from '../../../../../commons/src/components/input-field'
import LabeledCheckbox from '../../../../../commons/src/components/labeled-checkbox'
import Modal from '../../../../../commons/src/components/modal'
import Paragraph from '../../../../../commons/src/components/paragraph'
import Stack from '../../../../../commons/src/components/stack'

import {Order} from '../../../order-types'

type Props = {
  meta: ModalMeta
  order: Order
  onDone: (trackingNumber?: string) => Promise<void>
}

const urlRegex = /^(https?:\/\/)?((www\.)?[^.]+(\.[^.]+)+)$/

const AddTrackingModal = ({meta, order, onDone}: Props) => {
  const dispatch = useAppDispatch()

  const memoizedTrackingInfoHistory = useMemo(() => getTrackingInfoHistory(order), [order])

  const hasNoTrackingInfo =
    !order.printingService.trackingNumber && !order.printingService.trackingUrl

  const formik = useFormik({
    initialValues: {
      trackingNumber: '',
      personalDelivery: false,
      formikGeneral: '',
    },
    validationSchema: Yup.object({
      trackingNumber: Yup.string()
        .label('Tracking number')
        .test('notUrl', 'Tracking number cannot be an url', (val, ctx) => {
          if (val && urlRegex.test(val)) {
            return ctx.createError({
              path: `trackingNumber`,
            })
          }
          return true
        }),
      personalDelivery: Yup.boolean(),
    }),
    onSubmit: async (values, {setFieldError}) => {
      if (
        (!!values.trackingNumber || values.personalDelivery) &&
        !(!!values.trackingNumber && values.personalDelivery)
      ) {
        onDone(values.trackingNumber ? values.trackingNumber : undefined)
      } else {
        setFieldError(
          'formikGeneral',
          hasNoTrackingInfo
            ? 'Please enter either a tracking number or toggle the checkbox.'
            : 'Tracking number is required'
        )
      }
    },
  })

  return (
    <Modal
      size="xl"
      fullscreenMobile={meta.fullscreenMobile}
      headline={<Headline label="Tracking information" size="l" />}
      onClose={() => dispatch(closeModal())}
      buttons={
        <>
          <Button key="cancel" hierarchy="ghost" onClick={() => dispatch(closeModal())}>
            Cancel
          </Button>
          <Button
            key="submit"
            onClick={() => formik.handleSubmit()}
            icon={<Icon source={sendIcon} />}
            iconAlign="right"
          >
            Submit
          </Button>
        </>
      }
    >
      <Stack space="400">
        {!hasNoTrackingInfo && (
          <Stack>
            <Headline label="History" size="s" className="u-no-margin-bottom" />
            <Stack space="80">
              {memoizedTrackingInfoHistory.map((historyItem, index) => (
                <ActionBar key={index}>
                  <Paragraph size="s" minor className="u-no-margin-bottom">
                    {formatDateTime(historyItem.date)}
                  </Paragraph>
                  <Stack space="60">
                    {historyItem.trackingNumber && (
                      <Paragraph size="s" strong className="u-no-margin-bottom">
                        {historyItem.trackingNumber}
                      </Paragraph>
                    )}
                    {historyItem.trackingUrl && (
                      <Paragraph size="s" className="u-no-margin-bottom">
                        {historyItem.trackingUrl}
                      </Paragraph>
                    )}
                  </Stack>
                </ActionBar>
              ))}
            </Stack>
          </Stack>
        )}
        {hasNoTrackingInfo && (
          <Paragraph className="u-no-margin-bottom">
            Please provide a tracking <strong>number</strong> for this order.
            <br />
            If the order does not have a tracking number, please indicate so by checking the
            checkbox below.
          </Paragraph>
        )}
        <form onSubmit={formik.handleSubmit}>
          <Stack space="80">
            <Headline label="Add new tracking number" size="s" className="u-no-margin-bottom" />
            <FormRow layout="thirds">
              <FieldNote
                appearance="error"
                note={formik.submitCount > 0 && formik.errors.trackingNumber}
              >
                <InputField
                  placeholder="Tracking Number"
                  name="trackingNumber"
                  value={formik.values.trackingNumber}
                  appearance={
                    formik.submitCount > 0 && !!formik.errors.trackingNumber ? 'error' : undefined
                  }
                  onChange={(v, n, event) => formik.handleChange(event)}
                  onBlur={formik.handleBlur}
                />
              </FieldNote>
            </FormRow>

            {hasNoTrackingInfo && (
              <LabeledCheckbox
                name="personalDelivery"
                label="Order does not have a tracking number"
                checked={formik.values.personalDelivery}
                onChange={(value, name) => {
                  formik.setFieldValue(name as string, value)
                }}
                onBlur={formik.handleBlur}
              />
            )}
          </Stack>
        </form>
        {!!formik.errors.formikGeneral && (
          <Paragraph size="s" className="u-no-margin-bottom" error>
            {formik.errors.formikGeneral}
          </Paragraph>
        )}
      </Stack>
    </Modal>
  )
}

export default AddTrackingModal
